import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _create_class from "@swc/helpers/src/_create_class.mjs";
import _inherits from "@swc/helpers/src/_inherits.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import _create_super from "@swc/helpers/src/_create_super.mjs";
import Router, { withRouter } from "next/router";
import * as React from "react";
import { compose } from "recompose";
import { format } from "url";
var MINUTES_TO_MILLIS = 1000 * 60;
var TEN_SECONDS_TO_MILLIS = 1000 * 10;
var MAX_HISTORY_LENGTH = 100;
var DEFAULT_SELECTED_TIME = 3;
var TIME_SELECTORS = [
    {
        minutes: 30,
        label: "30m"
    },
    {
        minutes: 60,
        label: "1h"
    },
    {
        minutes: 60 * 4,
        label: "4h"
    },
    {
        minutes: 60 * 8,
        label: "8h"
    },
    {
        minutes: 60 * 16,
        label: "16h"
    },
    {
        minutes: 60 * 24,
        label: "24h"
    },
    {
        minutes: 60 * 48,
        label: "48h"
    },
    {
        minutes: 60 * 24 * 7,
        label: "1w"
    },
    {
        minutes: 60 * 24 * 30,
        label: "1m",
        largeRange: true
    }, 
];
// Support 10s, 30s, 60s, 5m and 10m.
var pollIntervals = [
    10000,
    30000,
    60000,
    300000,
    600000
];
var roundTo10Seconds = function(date) {
    return new Date(Math.round(date.getTime() / TEN_SECONDS_TO_MILLIS) * TEN_SECONDS_TO_MILLIS);
};
var floorTo10Seconds = function(date) {
    return new Date(Math.floor(date.getTime() / TEN_SECONDS_TO_MILLIS) * TEN_SECONDS_TO_MILLIS);
};
var ceilTo10Seconds = function(date) {
    return new Date(Math.ceil(date.getTime() / TEN_SECONDS_TO_MILLIS) * TEN_SECONDS_TO_MILLIS);
};
export var TimepickerContext = /*#__PURE__*/ React.createContext({
    to: new Date(),
    from: new Date(Date.now() - 8..hours),
    open: false,
    selectedTimeSelector: DEFAULT_SELECTED_TIME,
    selectedTimeHistory: [],
    prevSelectedTimeSelector: DEFAULT_SELECTED_TIME,
    timeSelectors: TIME_SELECTORS,
    isLive: false,
    nextLiveUpdate: null,
    liveIntervals: pollIntervals,
    liveIntervalIndex: -1,
    closePicker: function() {
        return;
    },
    openPicker: function() {
        return;
    },
    setTime: function(start, end, disableLive) {
        return;
    },
    goBackInSelectedTimeHistory: function() {
        return;
    },
    setSelectedTimeselector: function(index) {
        return;
    },
    resetSelectedTimeselector: function() {
        return;
    },
    startLive: function(liveIntervalIndex, cb) {
        return;
    },
    stopLive: function(cb) {
        return;
    }
});
var isASelectedTime = function(start, end) {
    // Check if the selected interval still fits a timeSelector, or is a custom interval
    var selectedTimeSelector = DEFAULT_SELECTED_TIME;
    // If end is not within a 10 minutes of now(), its a custom time
    if (Math.abs(new Date().getTime() - end.getTime()) > 10 * MINUTES_TO_MILLIS) {
        // Select custom time selector interval
        selectedTimeSelector = TIME_SELECTORS.length;
    } else {
        var msDiff = ceilTo10Seconds(new Date(end)).getTime() - floorTo10Seconds(new Date(start)).getTime();
        selectedTimeSelector = TIME_SELECTORS.findIndex(function(sel) {
            // Use a buffer range of 5 minutes when checking if it matches the selection.
            var bufferRange = 5 * MINUTES_TO_MILLIS;
            return sel.minutes * MINUTES_TO_MILLIS <= msDiff + bufferRange && sel.minutes * MINUTES_TO_MILLIS >= msDiff - bufferRange;
        });
        if (selectedTimeSelector === -1) {
            selectedTimeSelector = TIME_SELECTORS.length;
        }
    }
    return selectedTimeSelector;
};
var TimepickerContextWrapper = /*#__PURE__*/ function(_Component) {
    "use strict";
    _inherits(TimepickerContextWrapper, _Component);
    var _super = _create_super(TimepickerContextWrapper);
    function TimepickerContextWrapper(props) {
        _class_call_check(this, TimepickerContextWrapper);
        var _this;
        _this = _super.call(this, props);
        _this.checkLiveProgress = function() {
            var _state = _this.state, nextLiveUpdate = _state.nextLiveUpdate, liveIntervalIndex = _state.liveIntervalIndex;
            // Only live update if we are past the time the next update is scheduled for, and we
            // are not already loading data.
            if (nextLiveUpdate && nextLiveUpdate < new Date()) {
                if (_this.state.from) {
                    _this.setFromTo(_this.state.from, nextLiveUpdate, {
                        nextLiveUpdate: roundTo10Seconds(new Date(Date.now() + pollIntervals[liveIntervalIndex]))
                    }, false);
                } else {
                    console.error("Missing a from time! Cannot perform live update");
                }
            } else if (!nextLiveUpdate) {
                if (_this.liveTimer) {
                    clearInterval(_this.liveTimer);
                    delete _this.liveTimer;
                }
            }
        };
        var query = props.router.query;
        var initialFrom = new Date(query.from);
        initialFrom = isFinite(initialFrom.getTime()) ? initialFrom : floorTo10Seconds(new Date(new Date().getTime() - TIME_SELECTORS[DEFAULT_SELECTED_TIME].minutes * MINUTES_TO_MILLIS));
        var initialTo = new Date(query.to);
        initialTo = isFinite(initialTo.getTime()) ? initialTo : ceilTo10Seconds(new Date());
        var selectedTimeSelector = isASelectedTime(initialFrom, initialTo);
        _this.state = {
            from: initialFrom,
            to: initialTo,
            isLive: false,
            liveIntervalIndex: -1,
            open: false,
            liveIntervals: pollIntervals,
            selectedTimeSelector: selectedTimeSelector,
            nextLiveUpdate: null,
            selectedTimeHistory: [],
            prevSelectedTimeSelector: DEFAULT_SELECTED_TIME,
            timeSelectors: TIME_SELECTORS,
            closePicker: function() {
                _this.setState({
                    open: false
                });
            },
            openPicker: function() {
                _this.setState({
                    open: true
                });
            },
            setTime: function(start, end) {
                var disableLive = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
                if (disableLive) {
                    if (_this.liveTimer) {
                        clearInterval(_this.liveTimer);
                        delete _this.liveTimer;
                    }
                }
                if (!start) {
                    return;
                }
                if (end === undefined) {
                    var from;
                    if (_this.state.from !== null && _this.state.to !== null) {
                        from = new Date(start.getTime() - (new Date(_this.state.to).getTime() - new Date(_this.state.from).getTime()));
                    }
                    if (_this.state.selectedTimeSelector !== TIME_SELECTORS.length) {
                        from = new Date(start.getTime() - TIME_SELECTORS[_this.state.selectedTimeSelector].minutes * MINUTES_TO_MILLIS);
                    }
                    if (from) {
                        _this.setFromTo(from, start, {
                            open: false,
                            nextLiveUpdate: disableLive ? null : _this.state.nextLiveUpdate,
                            liveIntervalIndex: disableLive ? -1 : _this.state.liveIntervalIndex,
                            isLive: disableLive ? false : _this.state.isLive
                        });
                    }
                } else {
                    if (!end || end.getTime() > new Date().getTime()) {
                        end = new Date();
                    }
                    // Check if the selected interval still fits a timeSelector, or is a custom interval
                    var updatedSelectedTimeSelector = isASelectedTime(start, end);
                    _this.setFromTo(start, end, {
                        open: false,
                        selectedTimeSelector: updatedSelectedTimeSelector,
                        prevSelectedTimeSelector: _this.state.selectedTimeSelector,
                        nextLiveUpdate: disableLive ? null : _this.state.nextLiveUpdate,
                        liveIntervalIndex: disableLive ? -1 : _this.state.liveIntervalIndex,
                        isLive: disableLive ? false : _this.state.isLive
                    });
                }
            },
            goBackInSelectedTimeHistory: function() {
                if (_this.state.selectedTimeHistory.length > 0) {
                    var currentHistory = _to_consumable_array(_this.state.selectedTimeHistory);
                    var revertTime = currentHistory.shift();
                    // If the current time is the latest entry in the history, we need to shift one more time.
                    if (revertTime && _this.state.from && _this.state.to && revertTime.from.toISOString() === _this.state.from.toISOString() && revertTime.to.toISOString() === _this.state.to.toISOString()) {
                        revertTime = currentHistory.shift();
                    }
                    if (revertTime) {
                        _this.setFromTo(revertTime.from, revertTime.to, {
                            selectedTimeHistory: currentHistory,
                            selectedTimeSelector: revertTime.selectedTimeSelector !== undefined && revertTime.selectedTimeSelector !== null ? revertTime.selectedTimeSelector : _this.state.selectedTimeSelector,
                            prevSelectedTimeSelector: revertTime.selectedTimeSelector !== undefined && revertTime.selectedTimeSelector !== null ? _this.state.selectedTimeSelector : _this.state.prevSelectedTimeSelector
                        });
                    }
                }
            },
            setSelectedTimeselector: function(index) {
                var from = _this.state.from;
                if (index !== TIME_SELECTORS.length) {
                    from = new Date(new Date().getTime() - TIME_SELECTORS[index].minutes * MINUTES_TO_MILLIS);
                }
                if (from) {
                    _this.setFromTo(from, new Date(), {
                        prevSelectedTimeSelector: _this.state.selectedTimeSelector,
                        selectedTimeSelector: index
                    });
                } else {
                    _this.setState({
                        prevSelectedTimeSelector: _this.state.selectedTimeSelector,
                        selectedTimeSelector: index
                    });
                }
            },
            resetSelectedTimeselector: function() {
                var from = _this.state.from;
                if (_this.state.prevSelectedTimeSelector !== TIME_SELECTORS.length + 1) {
                    from = new Date(new Date().getTime() - TIME_SELECTORS[_this.state.prevSelectedTimeSelector].minutes * MINUTES_TO_MILLIS);
                }
                if (from) {
                    _this.setFromTo(from, new Date(), {
                        selectedTimeSelector: _this.state.prevSelectedTimeSelector
                    });
                } else {
                    _this.setState({
                        selectedTimeSelector: _this.state.prevSelectedTimeSelector
                    });
                }
            },
            startLive: function(liveIntervalIndex, cb) {
                // Verify that interval index is within possible values
                if (liveIntervalIndex !== -1 && liveIntervalIndex < pollIntervals.length) {
                    if (!_this.liveTimer) {
                        // Start timer
                        _this.liveTimer = setInterval(_this.checkLiveProgress, 1000);
                    }
                }
                _this.setState({
                    liveIntervalIndex: liveIntervalIndex,
                    isLive: true,
                    nextLiveUpdate: roundTo10Seconds(new Date(new Date().getTime() + pollIntervals[liveIntervalIndex]))
                });
                if (cb) {
                    cb();
                }
            },
            stopLive: function(cb) {
                if (_this.state.liveIntervalIndex !== -1 || _this.state.isLive !== false || _this.state.nextLiveUpdate !== null) {
                    _this.setState({
                        liveIntervalIndex: -1,
                        isLive: false,
                        nextLiveUpdate: null
                    });
                }
                if (_this.liveTimer) {
                    clearInterval(_this.liveTimer);
                    delete _this.liveTimer;
                }
                if (cb) {
                    cb();
                }
            }
        };
        return _this;
    }
    _create_class(TimepickerContextWrapper, [
        {
            /**
     * We are very conservative with our component causing rerenders, since it is very often wrapping
     * a significant amount of components.
     */ key: "shouldComponentUpdate",
            value: function shouldComponentUpdate(nextProps, nextState) {
                if (this.state.from !== nextState.from || this.state.to !== nextState.to || this.state.open !== nextState.open || this.state.selectedTimeSelector !== nextState.selectedTimeSelector || this.state.liveIntervalIndex !== nextState.liveIntervalIndex) {
                    return true;
                }
                if (this.props.children !== nextProps.children) {
                    return true;
                }
                return false;
            }
        },
        {
            key: "componentWillUnmount",
            value: function componentWillUnmount() {
                this.state.stopLive();
            }
        },
        {
            key: "render",
            value: function render() {
                var children = this.props.children;
                return /*#__PURE__*/ React.createElement(TimepickerContext.Provider, {
                    value: this.state
                }, children);
            }
        },
        {
            key: "addTimeToHistory",
            value: function addTimeToHistory(from, to, selectedTimeSelector) {
                var currentHistory = _to_consumable_array(this.state.selectedTimeHistory);
                if (!from || !to) {
                    return currentHistory;
                }
                // If the first element matches the date we just got, do not add it to the history.
                if (currentHistory[0] && currentHistory[0].from.toISOString() === from.toISOString() && currentHistory[0].to.toISOString() === to.toISOString()) {
                    return currentHistory;
                } else {
                    // If we are growing above our history length, remove the last element.
                    if (currentHistory.length >= MAX_HISTORY_LENGTH) {
                        currentHistory.pop();
                    }
                    currentHistory.unshift({
                        from: from,
                        to: to,
                        selectedTimeSelector: selectedTimeSelector
                    });
                }
                return currentHistory;
            }
        },
        {
            key: "setFromTo",
            value: function setFromTo(from, to) {
                var newState = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {}, addToHistory = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : true;
                var _router = this.props.router, pathname = _router.pathname, query = _router.query;
                if (!newState.selectedTimeHistory && addToHistory) {
                    newState.selectedTimeHistory = this.addTimeToHistory(this.state.from, this.state.to, this.state.selectedTimeSelector);
                }
                // The router update here will trigger `getDerivedStateFromProps` that updates the from and to of the context.
                var url = format({
                    pathname: pathname,
                    query: Object.assign(Object.assign({}, query), {
                        from: floorTo10Seconds(from).toISOString(),
                        to: ceilTo10Seconds(to).toISOString()
                    })
                });
                Router.replace(url, url, {
                    shallow: true
                });
                this.setState(Object.assign(Object.assign({}, newState), {
                    from: floorTo10Seconds(from),
                    to: ceilTo10Seconds(to)
                }));
            }
        }
    ], [
        {
            key: "getDerivedStateFromProps",
            value: // We use `getDerivedStateFromProps` to trigger time changes when the router updates.
            function getDerivedStateFromProps(props, state) {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
                var routerFrom = Array.isArray((_b = (_a = props === null || props === void 0 ? void 0 : props.router) === null || _a === void 0 ? void 0 : _a.query) === null || _b === void 0 ? void 0 : _b.from) ? (_e = (_d = (_c = props === null || props === void 0 ? void 0 : props.router) === null || _c === void 0 ? void 0 : _c.query) === null || _d === void 0 ? void 0 : _d.from) === null || _e === void 0 ? void 0 : _e[0] : (_g = (_f = props === null || props === void 0 ? void 0 : props.router) === null || _f === void 0 ? void 0 : _f.query) === null || _g === void 0 ? void 0 : _g.from;
                var routerTo = Array.isArray((_j = (_h = props === null || props === void 0 ? void 0 : props.router) === null || _h === void 0 ? void 0 : _h.query) === null || _j === void 0 ? void 0 : _j.to) ? (_m = (_l = (_k = props === null || props === void 0 ? void 0 : props.router) === null || _k === void 0 ? void 0 : _k.query) === null || _l === void 0 ? void 0 : _l.to) === null || _m === void 0 ? void 0 : _m[0] : (_p = (_o = props === null || props === void 0 ? void 0 : props.router) === null || _o === void 0 ? void 0 : _o.query) === null || _p === void 0 ? void 0 : _p.to;
                if (!routerFrom || !routerTo) {
                    return null;
                }
                var routesDoNotMatchState = state.from === null && routerFrom || state.from !== null && new Date(routerFrom).getTime() !== ((_q = state.from) === null || _q === void 0 ? void 0 : _q.getTime()) || state.to === null && routerTo || state.to !== null && new Date(routerTo).getTime() !== ((_r = state.to) === null || _r === void 0 ? void 0 : _r.getTime());
                if ((routerFrom || routerTo) && routesDoNotMatchState) {
                    return {
                        from: new Date(routerFrom),
                        to: new Date(routerTo)
                    };
                }
                return null;
            }
        }
    ]);
    return TimepickerContextWrapper;
}(React.Component);
var enhance = compose(withRouter);
export default enhance(TimepickerContextWrapper);
